import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api";
import { useToast } from "../ToastContext";
import { LoadingContext } from "../LoadingContext";
import { jwtDecode } from "jwt-decode";

export interface UserProps {
  password: string;
  email: string;
  type: number;
}

export interface FormPassword {
  password: string;
  confirmPassword: string;
}

export interface Empresa {
  name: string;
  email: string;
  user: {
    id: number;
    createdDate: string;
    updatedDate: string;
    name: string;
    email: string;
    username: string;
    password: string;
    type: number;
    status: number;
  };
}

export interface UserToken {
  unique_name: string;
  Type: "Empresa" | "Instituicao" | "Aluno" | string;
  EmpresaId: number;
  UserId: number;
  IsManager: boolean;
  exp?: number; 
  iat?: number;
}

type UserTypeCheck = 1 | 2 | 3 | 4;

function getUserTypeCheckFromTokenType(type?: string): UserTypeCheck {
  if (type === "Empresa") return 1;
  if (type === "Instituicao") return 2;
  if (type === "Aluno") return 3;
  return 1;
}

function safeDecodeToken(token: string): UserToken | null {
  try {
    return jwtDecode(token) as UserToken;
  } catch {
    return null;
  }
}

function isTokenValid(token: string): boolean {
  const decoded = safeDecodeToken(token);
  if (!decoded) return false;

  if (!decoded.exp) return false;

  const nowSec = Math.floor(Date.now() / 1000);
  return decoded.exp > nowSec;
}

function getTokenRemainingMs(token: string): number {
  const decoded = safeDecodeToken(token);
  if (!decoded?.exp) return 0;
  const nowMs = Date.now();
  const expMs = decoded.exp * 1000;
  return Math.max(0, expMs - nowMs);
}

export default function useAuth() {
  const [data, setData] = useState<UserProps>({
    password: "",
    email: "",
    type: 0,
  });

  const [formPassword, setFormPassword] = useState<FormPassword>({
    password: "",
    confirmPassword: "",
  });

  const [authenticated, setAuthenticated] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);

  const { setLoading } = useContext(LoadingContext);
  const navigate = useNavigate();
  const showToast = useToast();

  const logoutTimerRef = useRef<number | null>(null);

  const tokenLocal = localStorage.getItem("token");

  const objUserTk: UserToken = tokenLocal
    ? (safeDecodeToken(tokenLocal) as UserToken) || ({} as UserToken)
    : ({} as UserToken);

  const [selectTypeUser, setSelectTypeUser] = useState<UserTypeCheck>(() => {
    return getUserTypeCheckFromTokenType(objUserTk?.Type);
  });

  function clearLogoutTimer() {
    if (logoutTimerRef.current) {
      window.clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = null;
    }
  }

  function scheduleLogoutFromToken(token: string) {
    clearLogoutTimer();
    const remainingMs = getTokenRemainingMs(token);
    if (!remainingMs) return;

    logoutTimerRef.current = window.setTimeout(() => {
      handleLogout(true);
    }, remainingMs);
  }

  function handleSetUser({ password, email }: UserProps) {
    setData({ password, email, type: selectTypeUser });
  }

  async function empresaRegister(empresaCreateRequest: Empresa) {
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      await api.post("/Empresa/create", empresaCreateRequest, { headers });
      showToast("success", "Empresa cadastrado com sucesso!");
      navigate("/auth/login");
    } catch (error) {
      showToast("error", "Erro ao cadastrar empresa. Tente novamente.");
    }
  }

  async function handleLogin({ email, password }: UserProps) {
    try {
      setLoadingButton(true);

      const userType = selectTypeUser;

      const {
        data: { token },
      } = await api.post("/Users/authenticate", { email, password, type: userType });

      if (!token || !isTokenValid(token)) {
        showToast("error", "Token inválido recebido. Tente novamente.");
        return;
      }

      localStorage.setItem("token", token);
      api.defaults.headers.Authorization = `Bearer ${token}`;

      const decoded = safeDecodeToken(token);
      setSelectTypeUser(getUserTypeCheckFromTokenType(decoded?.Type));

      setAuthenticated(true);
      scheduleLogoutFromToken(token);

      navigate("/admin/index");
    } catch (error) {
      showToast("error", "Erro ao efetuar o login. Verifique suas credenciais.");
    } finally {
      setLoadingButton(false);
    }
  }

  async function updateUserPassword(userId: number, newPassword: string) {
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      await api.patch(`/Users/${userId}/update-password`, JSON.stringify(newPassword), { headers });
      showToast("success", "Senha atualizada com sucesso.");
    } catch (error) {
      showToast("error", "Erro ao atualizar a senha. Verifique novamente mais tarde.");
      console.error("Erro ao atualizar a senha:", error);
    } finally {
      setLoading(false);
    }
  }

  async function sendResetPasswordEmail(email: string) {
    try {
      await api.post("/Users/reset-password", { email });
      showToast("success", "E-mail de redefinição de senha enviado.");
    } catch (error) {
      showToast("error", "Erro ao enviar o e-mail de redefinição de senha.");
    }
  }

  async function verifyResetCode(email: string, code: string): Promise<boolean> {
    try {
      const response = await api.post("/Users/verify-reset-code", { email, code });
      return response.data.isValid;
    } catch (error) {
      console.error("Erro ao verificar código de redefinição:", error);
      return false;
    }
  }

  async function handleLogout(isExpired = false) {
    clearLogoutTimer();

    setAuthenticated(false);
    localStorage.removeItem("token");
    api.defaults.headers.Authorization = null;

    if (isExpired) {
      showToast("info", "Sua sessão expirou. Faça login novamente.");
    }

    navigate("/auth/login");
  }

  useEffect(() => {
    setLoadingButton(true);

    const token = localStorage.getItem("token");

    if (!token) {
      setAuthenticated(false);
      api.defaults.headers.Authorization = null;
      setLoadingButton(false);
      return;
    }

    if (!isTokenValid(token)) {
      handleLogout(true);
      setLoadingButton(false);
      return;
    }

    api.defaults.headers.Authorization = `Bearer ${token}`;
    setAuthenticated(true);

    const decoded = safeDecodeToken(token);
    setSelectTypeUser(getUserTypeCheckFromTokenType(decoded?.Type));

    scheduleLogoutFromToken(token);

    setLoadingButton(false);

    return () => clearLogoutTimer();
  }, []);

  return {
    authenticated,
    loadingButton,
    empresaRegister,
    handleLogin,
    handleLogout,
    handleSetUser,
    data,
    objUserTk,
    selectTypeUser,
    setSelectTypeUser,
    formPassword,
    setFormPassword,
    updateUserPassword,
    sendResetPasswordEmail,
    verifyResetCode,
  };
}